import { createContext, useCallback, useContext, useMemo, useState } from "react";

import type React from "react";
import type { ChoiceListsState } from "../models/patientEncounter";

interface NewEncounterContextType {
  showNewEncounterDialog: boolean;
  openNewEncounterDialog: () => void;
  closeNewEncounterDialog: () => void;
  handleCreateNewEncounter: (
    choiceLists: ChoiceListsState,
    mrn?: string,
    facility?: string,
    encounterType?: string,
    accountNumber?: string,
  ) => void;
  buildNewEncounterChoiceLists: (choiceLists: ChoiceListsState, facility: string) => void;
  getFacilitiesChoiceList: () => void;
  getPracticeChoiceList: () => void;
  choiceLists: ChoiceListsState;
  encounterOpened: boolean;
}

interface NewEncounterProviderProps {
  children: React.ReactNode;
  choiceLists: ChoiceListsState;
  onCreateNewEncounter: (
    choiceLists: ChoiceListsState,
    mrn?: string,
    facility?: string,
    encounterType?: string,
    accountNumber?: string,
  ) => void;
  buildNewEncounterChoiceLists: (choiceLists: ChoiceListsState, facility: string) => void;
  getFacilitiesChoiceList: () => void;
  getPracticeChoiceList: () => void;
  encounterOpened?: boolean;
}

export const NewEncounterContext = createContext<NewEncounterContextType | undefined>(undefined);

export const NewEncounterProvider: React.FC<NewEncounterProviderProps> = ({
  children,
  choiceLists,
  onCreateNewEncounter,
  buildNewEncounterChoiceLists,
  getFacilitiesChoiceList,
  getPracticeChoiceList,
  encounterOpened = false,
}) => {
  const [showNewEncounterDialog, setShowNewEncounterDialog] = useState(false);

  const openNewEncounterDialog = useCallback(() => {
    setShowNewEncounterDialog(true);
  }, []);

  const closeNewEncounterDialog = useCallback(() => {
    setShowNewEncounterDialog(false);
  }, []);

  const handleCreateNewEncounter = useCallback(
    (
      lists: ChoiceListsState,
      mrn?: string,
      facility?: string,
      encounterType?: string,
      accountNumber?: string,
    ) => {
      onCreateNewEncounter(lists, mrn, facility, encounterType, accountNumber);
    },
    [onCreateNewEncounter],
  );

  const value = useMemo(
    () => ({
      showNewEncounterDialog,
      openNewEncounterDialog,
      closeNewEncounterDialog,
      handleCreateNewEncounter,
      buildNewEncounterChoiceLists,
      getFacilitiesChoiceList,
      getPracticeChoiceList,
      choiceLists,
      encounterOpened,
    }),
    [
      showNewEncounterDialog,
      openNewEncounterDialog,
      closeNewEncounterDialog,
      handleCreateNewEncounter,
      buildNewEncounterChoiceLists,
      getFacilitiesChoiceList,
      getPracticeChoiceList,
      choiceLists,
      encounterOpened,
    ],
  );

  return <NewEncounterContext.Provider value={value}>{children}</NewEncounterContext.Provider>;
};

export const useNewEncounter = () => {
  const context = useContext(NewEncounterContext);
  if (context === undefined) {
    throw new Error("useNewEncounter must be used within a NewEncounterProvider");
  }
  return context;
};
